import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { MenuIcon, XIcon } from 'lucide-react';
import { Logo } from './Logo';
import { ButtonLink } from '../ButtonLink';
import { mainNav } from '../../data/navigation';
import { visibleNavItems } from '../../utils/features';
const EASE = [0.23, 1, 0.32, 1] as const;
export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const items = visibleNavItems(mainNav);
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return <header className="sticky top-0 z-40 bg-midnight-950/95 text-champagne-100 backdrop-blur" data-id="element-127" data-mp="6vyd3ig9j69i">
      <div className="mx-auto flex h-20 max-w-site items-center justify-between px-6 lg:px-10" data-id="element-128" data-mp="bui1rqm8qw5k">
        <Logo data-id="element-129" data-mp="glqccpzb4nkb" />
        <div className="flex items-center gap-3" data-id="element-130" data-mp="gkyibjlcyxw5">
          <ButtonLink to="/contact?type=employer" className="hidden sm:inline-flex" data-id="element-131" data-mp="gm9ry01hizv6">
            Partner With Us
          </ButtonLink>
          <button type="button" onClick={() => setOpen(true)} className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-champagne-300/25 text-champagne-100 transition-colors duration-150 ease-out hover:border-champagne-300/60 xl:hidden" aria-label="Open menu" aria-expanded={open} aria-controls="mobile-nav" data-id="element-132" data-mp="3k2gnfj9qxqsv">
            <MenuIcon className="h-5 w-5" aria-hidden="true" data-id="element-133" data-mp="1994140suqctc" />
          </button>
        </div>
      </div>

      <nav aria-label="Main" className="hidden border-t border-champagne-100/10 xl:block" data-id="element-134" data-mp="1dapnqdv6u5gq">
        <ul className="mx-auto flex h-12 max-w-site items-center justify-between px-10" data-id="element-135" data-mp="3abbxz1zut1bv">
          {items.map(item => <li key={item.to} data-id="element-136" data-mp="35cs84qi535e8">
              <NavLink to={item.to} end={item.to === '/'} className={({
            isActive
          }) => `relative whitespace-nowrap py-3 text-[13px] font-medium tracking-wide transition-colors duration-150 ease-out ${isActive ? 'text-champagne-300' : 'text-champagne-100/75 hover:text-champagne-50'}`} data-id="element-137" data-mp="35fmxmwg0fgei">
                {({
              isActive
            }) => <>
                    {item.label}
                    {isActive && <span className="absolute inset-x-0 -bottom-px h-px bg-champagne-300" aria-hidden="true" data-id="element-138" data-mp="2vn8pnlyc9djw" />}
                  </>}
              </NavLink>
            </li>)}
        </ul>
      </nav>

      <AnimatePresence data-id="element-139" data-mp="22y1qckr28jxl">
        {open && <>
            <motion.div className="fixed inset-0 z-40 bg-midnight-950/70 xl:hidden" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: EASE
        }} onClick={() => setOpen(false)} aria-hidden="true" data-id="element-140" data-mp="ig93tpw53a4c" />
            <motion.div id="mobile-nav" role="dialog" aria-modal="true" aria-label="Site navigation" className="fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col bg-midnight-950 px-6 pb-8 xl:hidden" initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          duration: 0.28,
          ease: EASE
        }} data-id="element-141" data-mp="ed31gtc5e335">
              <div className="flex h-20 items-center justify-between" data-id="element-142" data-mp="3cvrsozhc72z6">
                <Logo onClick={() => setOpen(false)} variant="flat" data-id="element-143" data-mp="3hvmkr6culylt" />
                <button type="button" onClick={() => setOpen(false)} className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-champagne-300/25 text-champagne-100" aria-label="Close menu" data-id="element-144" data-mp="3httcjmml76bp">
                  <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-145" data-mp="3oav1cpefp4o6" />
                </button>
              </div>
              <ul className="mt-4 flex-1 overflow-y-auto" data-id="element-146" data-mp="jz0y8ka36bw9">
                {items.map(item => <li key={item.to} className="border-b border-champagne-100/10" data-id="element-147" data-mp="fxfp9a0oahrc">
                    <NavLink to={item.to} end={item.to === '/'} className={({
                isActive
              }) => `block py-3.5 font-serif text-2xl transition-colors duration-150 ease-out ${isActive ? 'text-champagne-300' : 'text-champagne-50 hover:text-champagne-200'}`} data-id="element-148" data-mp="fvl53xx86tob">
                      {item.label}
                    </NavLink>
                  </li>)}
              </ul>
              <ButtonLink to="/contact?type=employer" className="mt-6 w-full" data-id="element-149" data-mp="2llersu23baiw">
                Partner With Us
              </ButtonLink>
            </motion.div>
          </>}
      </AnimatePresence>
    </header>;
}